import React, { useEffect, useState, useCallback } from "react";
import { TL } from "../i18n";
import { useApp } from "../context/AppContext";
import client from "../lib/api";
import { Wrench, RefreshCw, X } from "lucide-react";

export default function RepairBanner() {
  const { user, isAdmin, isSupport } = useApp();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState("");
  const [done, setDone] = useState("");
  const active = user && !isAdmin && !isSupport;

  const load = useCallback(() => {
    if (!active) return;
    client.get("/repair/pending").then((r) => setItems(r.data)).catch(() => {});
  }, [active]);

  useEffect(() => {
    if (!active) return;
    let id;
    // Opt-in gate: only poll for repairs if the runner has explicitly enabled support-assisted restore.
    client.get("/repair/settings").then((r) => {
      if (!r.data?.enabled) return;
      load();
      id = setInterval(load, 60000);
    }).catch(() => {});
    return () => { if (id) clearInterval(id); };
  }, [active, load]);

  if (!active || items.length === 0) return null;

  const apply = async (d) => {
    setBusy(d.id); setDone("");
    try {
      const { data } = await client.post(`/repair/${d.id}/apply`);
      setDone(`Repair complete for ${d.app_label}: ${data.restored} item(s) restored.`);
      setItems((x) => x.filter((i) => i.id !== d.id));
      setTimeout(() => window.location.reload(), 1400);
    } catch { setBusy(""); }
  };
  const dismiss = async (d) => {
    setBusy(d.id);
    try { await client.post(`/repair/${d.id}/dismiss`); setItems((x) => x.filter((i) => i.id !== d.id)); }
    finally { setBusy(""); }
  };

  return (
    <div className="mb-5 space-y-2" data-testid="repair-banner">
      {done && <p className="text-sm text-emerald-600" data-testid="repair-done">{done}</p>}
      {items.map((d) => (
        <div key={d.id} data-testid={`repair-item-${d.id}`}
          className="flex flex-wrap items-center gap-2 rounded-lg border border-[#0F62FE]/40 bg-[#0F62FE]/10 px-4 py-2.5 text-sm">
          <Wrench size={16} className="text-[#0F62FE]" />
          <div className="min-w-0 flex-1">
            <b>Support has prepared a repair for your {d.app_label} instance.</b>
            {d.note ? <span className="opacity-80"> — {d.note}</span> : null}
            <span className="block text-xs opacity-60">Restoring recovers your most recent saved data from live backup. Prepared by {d.created_by}.</span>
          </div>
          <button onClick={() => apply(d)} disabled={busy === d.id} data-testid={`repair-apply-${d.id}`}
            className="flex items-center gap-1.5 rounded-md bg-[#0F62FE] px-3 py-1.5 text-xs font-medium text-white lift disabled:opacity-60">
            <RefreshCw size={13} className={busy === d.id ? "animate-spin" : ""} /> {busy === d.id ? "Restoring…" : "Restore / repair now"}
          </button>
          <button onClick={() => dismiss(d)} disabled={busy === d.id} data-testid={`repair-dismiss-${d.id}`}
            className="rounded-md border border-black/15 px-2 py-1.5 text-xs lift hover:bg-black/5 dark:border-white/15 dark:hover:bg-white/10">
            <X size={13} />
          </button>
        </div>
      ))}
    </div>
  );
}
